import {
    ArcType,
    BlendingState,
    Cartesian2,
    Cartesian3,
    Cesium3DTileset,
    ClippingPlane,
    ClippingPlaneCollection,
    Color,
    defined,
    Model,
    ScreenSpaceEventHandler,
    ScreenSpaceEventType,
    Viewer,
    Matrix3,
    Matrix4,
    Transforms,
    Quaternion,
    SceneTransforms,
    Primitive,
    GeometryInstance,
    PolylineGeometry,
    PolylineMaterialAppearance,
    Material,
    PlaneGeometry,
    PlaneOutlineGeometry,
    ColorGeometryInstanceAttribute,
    PerInstanceColorAppearance,
    GeometryPipeline
} from "cesium";
import { showStatusStack } from "../utils/utils";
import { getMessage } from "../language";



// ==========================================
// CẤU HÌNH CÁC THÔNG SỐ HIỂN THỊ CỦA GIZMO
// ==========================================
export const GIZMO_STYLE = {
    // Bán kính tĩnh của các vòng xoay (mét)
    RING_RADIUS: 0.5,

    // Độ rộng (độ dày) nét vẽ của các đối tượng polyline khi bình thường và khi hover
    ARROW_WIDTH: 10.0,
    ARROW_WIDTH_HOVER: 13.0,
    RING_WIDTH: 2.0,
    RING_WIDTH_HOVER: 3.0,

    // Hệ số tự động phóng to/thu nhỏ theo khoảng cách camera
    SCALE_FACTOR_ARROW: 0.05,
    SCALE_FACTOR_PLANE: 0.08,

    // Màu sắc hiển thị của mặt phẳng hứng cắt ảo
    COLOR_PLANE: Color.GRAY.withAlpha(0.6),
    COLOR_PLANE_OUTLINE: Color.WHITE,

    // Màu sắc mặc định (bán trong suốt) của 3 mũi tên tịnh tiến (X, Y, Z)
    COLOR_ARROW_X: Color.RED.withAlpha(0.6),
    COLOR_ARROW_Y: Color.GREEN.withAlpha(0.6),
    COLOR_ARROW_Z: Color.BLUE.withAlpha(0.6),

    // Màu sắc mặc định (bán trong suốt) của 3 vòng xoay tương tác (X, Y, Z)
    COLOR_RING_X: Color.GREEN.withAlpha(0.5),
    COLOR_RING_Y: Color.RED.withAlpha(0.5),
    COLOR_RING_Z: Color.BLUE.withAlpha(0.5),

    // Màu sắc khi hover (rực rỡ và opaque hoàn toàn) của các mũi tên tịnh tiến
    COLOR_ARROW_X_HOVER: Color.RED,
    COLOR_ARROW_Y_HOVER: Color.GREEN,
    COLOR_ARROW_Z_HOVER: Color.BLUE,

    // Màu sắc khi hover (rực rỡ và opaque hoàn toàn) của các vòng xoay
    COLOR_RING_X_HOVER: Color.GREEN,
    COLOR_RING_Y_HOVER: Color.RED,
    COLOR_RING_Z_HOVER: Color.BLUE
};

export const GIZMO_IDS = {
    PLANE: "visual-clipping-plane",
    PLANE_OUTLINE: "visual-clipping-plane-outline",
    ARROW_X: "visual-clipping-arrow-x",
    ARROW_Y: "visual-clipping-arrow-y",
    ARROW_Z: "visual-clipping-arrow-z",
    RING_X: "visual-clipping-ring-x",
    RING_Y: "visual-clipping-ring-y",
    RING_Z: "visual-clipping-ring-z"
};

export const GIZMO_MATH = {
    EARTH_RADIUS_OFFSET: 6378137.0, // used for offsetMatrix to avoid point projection errors
    RING_SEGMENTS: 32,              // segments used to draw a 1/4 ring
    DENOMINATOR_TOLERANCE: 0.001,
    NORMAL_TOLERANCE: 0.0001,
    SCREEN_DRAG_SCALE: 0.0015
};

export class ClippingPlaneCesium {
    private _viewer: Viewer;
    private _handler: ScreenSpaceEventHandler | undefined;
    private _planePrimitive: Primitive | undefined;
    private _planeOutlinePrimitive: Primitive | undefined;
    private _arrowXPrimitive: Primitive | undefined;
    private _arrowXHoverPrimitive: Primitive | undefined;
    private _arrowYPrimitive: Primitive | undefined;
    private _arrowYHoverPrimitive: Primitive | undefined;
    private _arrowZPrimitive: Primitive | undefined;
    private _arrowZHoverPrimitive: Primitive | undefined;
    private _ringXPrimitive: Primitive | undefined;
    private _ringXHoverPrimitive: Primitive | undefined;
    private _ringYPrimitive: Primitive | undefined;
    private _ringYHoverPrimitive: Primitive | undefined;
    private _ringZPrimitive: Primitive | undefined;
    private _ringZHoverPrimitive: Primitive | undefined;
    private _removeListener: (() => void) | undefined;
    private _isActive: boolean = false;

    // Drag-to-slide state variables
    private _activeDragAxis: "x" | "y" | "z" | "plane" | "rot-x" | "rot-y" | "rot-z" | undefined = undefined;
    private _hoveredAxis: "x" | "y" | "z" | "rot-x" | "rot-y" | "rot-z" | undefined = undefined;
    private _dragNormal: Cartesian3 | undefined;
    private _dragStartBasePosition: Cartesian3 | undefined;
    private _dragStartNormal: Cartesian3 | undefined;
    private _dragStartRotation: Matrix3 | undefined;
    private _dragStartPlanePosition: Cartesian3 | undefined;
    private _basePosition: Cartesian3 | undefined;
    private _normal: Cartesian3 | undefined;
    private _planeRotation: Matrix3 = Matrix3.IDENTITY;
    private _isDragging: boolean = false;
    private _dragStartMousePosition: Cartesian2 = new Cartesian2();
    private _dragStartHeight: number = 0.0;
    private _dragStartS: number = 0.0;
    private _dragStartAngle2D: number = 0.0;
    private _currentHeight: number = 0.0;
    private _planePosition: Cartesian3 = new Cartesian3();
    private _primitiveInverseMatrices: Map<any, Matrix4> = new Map();
    private _mode: "up" | "east" | "north" = "up";
    private _isGizmoVisible: boolean = true;

    private _isInteractionPaused: boolean = false;

    // Saved gizmo state variables (for persistence across deactivate/activate)
    private _savedBasePosition: Cartesian3 | undefined = undefined;
    private _savedPlanePosition: Cartesian3 | undefined = undefined;
    private _savedPlaneRotation: Matrix3 | undefined = undefined;
    private _savedNormal: Cartesian3 | undefined = undefined;
    private _savedCurrentHeight: number = 0.0;
    private _savedMode: "up" | "east" | "north" = "up";

    // Flag to skip mode recalculation when restoring saved state
    private _justRestoredState: boolean = false;
    private _isClippingActive: boolean = false;
    private _containerId: string;
    private _statusStackItem: { remove: () => void } | undefined = undefined;

    public onPositionPlaced?: () => void;

    constructor(input: {
        viewer: Viewer,
        containerId: string
    }) {
        this._viewer = input.viewer;
        this._containerId = input.containerId;
    }

    public setInteractionPaused = (paused: boolean) => {
        this._isInteractionPaused = paused;
        if (paused) {
            this._hoveredAxis = undefined;
            this._updateGizmoModelMatrix();
            if (this._statusStackItem) {
                this._statusStackItem.remove();
                this._statusStackItem = undefined;
            }
            if (this._handler) {
                this._handler.destroy();
                this._handler = undefined;
            }
        } else {
            if (this._isActive && !this._statusStackItem) {
                this._statusStackItem = showStatusStack(this._containerId, getMessage("clipping-plane-state"));
            }
            if (this._isActive && !this._handler) {
                this._setupMouseHandlers();
            }
        }
    }

    private _saveGizmoState = () => {
        if (this._isClippingActive && defined(this._basePosition)) {
            this._savedBasePosition = Cartesian3.clone(this._basePosition, this._savedBasePosition);
            if (defined(this._planePosition)) {
                this._savedPlanePosition = Cartesian3.clone(this._planePosition, this._savedPlanePosition);
            }
            if (defined(this._planeRotation)) {
                this._savedPlaneRotation = Matrix3.clone(this._planeRotation, this._savedPlaneRotation);
            }
            if (defined(this._normal)) {
                this._savedNormal = Cartesian3.clone(this._normal, this._savedNormal);
            }
            this._savedCurrentHeight = this._currentHeight;
            this._savedMode = this._mode;
        } else {
            this._savedBasePosition = undefined;
            this._savedPlanePosition = undefined;
            this._savedPlaneRotation = undefined;
            this._savedNormal = undefined;
            this._savedCurrentHeight = 0.0;
            this._savedMode = "up";
        }
    }

    /**
     * Restores the gizmo state from saved values.
     */
    private _restoreGizmoState = () => {
        if (defined(this._savedBasePosition)) {
            this._basePosition = Cartesian3.clone(this._savedBasePosition);
            this._planePosition = Cartesian3.clone(this._savedPlanePosition!);
            this._planeRotation = Matrix3.clone(this._savedPlaneRotation!);
            this._normal = Cartesian3.clone(this._savedNormal!);
            this._currentHeight = this._savedCurrentHeight;
            this._mode = this._savedMode;
            this._justRestoredState = true;  // Set flag to skip mode recalculation
            this._isClippingActive = true;
            return true;
        }
        return false;
    }

    private _getRotatedLocalAxes(): { x: Cartesian3, y: Cartesian3, z: Cartesian3 } {
        if (!defined(this._planeRotation) || !defined(this._normal) || !defined(this._planePosition)) {
            return {
                x: new Cartesian3(1.0, 0.0, 0.0),
                y: new Cartesian3(0.0, 1.0, 0.0),
                z: new Cartesian3(0.0, 0.0, 1.0)
            };
        }

        return {
            x: Matrix3.getColumn(this._planeRotation, 0, new Cartesian3()),
            y: Matrix3.getColumn(this._planeRotation, 1, new Cartesian3()),
            z: Matrix3.getColumn(this._planeRotation, 2, new Cartesian3())
        };
    }

    private _getLocalRingPoints(axis: "x" | "y" | "z"): Cartesian3[] {
        const points: Cartesian3[] = [];
        const segments = GIZMO_MATH.RING_SEGMENTS;
        const radius = GIZMO_STYLE.RING_RADIUS;

        for (let i = 0; i <= segments; i++) {
            const theta = (i / segments) * (Math.PI / 2);
            const cos = Math.cos(theta);
            const sin = Math.sin(theta);

            if (axis === "x") {
                points.push(new Cartesian3(0.0, radius * cos, radius * sin));
            } else if (axis === "y") {
                points.push(new Cartesian3(radius * cos, 0.0, radius * sin));
            } else {
                points.push(new Cartesian3(radius * cos, radius * sin, 0.0));
            }
        }
        return points;
    }

    private _removeGizmoPrimitives = () => {
        if (defined(this._removeListener)) {
            this._removeListener();
            this._removeListener = undefined;
        }

        const primitivesToRemove = [
            this._planePrimitive,
            this._planeOutlinePrimitive,
            this._arrowXPrimitive,
            this._arrowXHoverPrimitive,
            this._arrowYPrimitive,
            this._arrowYHoverPrimitive,
            this._arrowZPrimitive,
            this._arrowZHoverPrimitive,
            this._ringXPrimitive,
            this._ringXHoverPrimitive,
            this._ringYPrimitive,
            this._ringYHoverPrimitive,
            this._ringZPrimitive,
            this._ringZHoverPrimitive
        ];

        primitivesToRemove.forEach((p) => {
            if (defined(p)) {
                this._viewer.scene.primitives.remove(p);
            }
        });

        this._planePrimitive = undefined;
        this._planeOutlinePrimitive = undefined;
        this._arrowXPrimitive = undefined;
        this._arrowXHoverPrimitive = undefined;
        this._arrowYPrimitive = undefined;
        this._arrowYHoverPrimitive = undefined;
        this._arrowZPrimitive = undefined;
        this._arrowZHoverPrimitive = undefined;
        this._ringXPrimitive = undefined;
        this._ringXHoverPrimitive = undefined;
        this._ringYPrimitive = undefined;
        this._ringYHoverPrimitive = undefined;
        this._ringZPrimitive = undefined;
        this._ringZHoverPrimitive = undefined;
    };

    private _createPolylineGizmo(
        positions: Cartesian3[],
        width: number,
        id: string,
        color: Color,
        offsetMatrix: Matrix4,
        isArrow: boolean
    ): Primitive | undefined {
        const geometryParam = new PolylineGeometry({
            positions: positions,
            width: width,
            arcType: ArcType.NONE,
            vertexFormat: PolylineMaterialAppearance.VERTEX_FORMAT
        });
        const geometry = PolylineGeometry.createGeometry(geometryParam);
        if (!defined(geometry)) return undefined;

        const instance = new GeometryInstance({
            geometry: geometry,
            id: id,
            modelMatrix: offsetMatrix
        });
        GeometryPipeline.transformToWorldCoordinates(instance);

        const primitive = new Primitive({
            geometryInstances: instance,
            appearance: new PolylineMaterialAppearance({
                material: Material.fromType(isArrow ? 'PolylineArrow' : 'Color', { color: color }),
                translucent: !isArrow,
                renderState: {
                    depthTest: {
                        enabled: false
                    },
                    depthMask: false
                }
            }),
            asynchronous: false
        });
        this._viewer.scene.primitives.add(primitive);
        return primitive;
    }

    private _updateGizmoModelMatrix = () => {
        if (!defined(this._viewer) || !defined(this._planePosition) || !defined(this._normal)) {
            return;
        }

        // 1. Calculate local orthonormal axes
        const dirs = this._getRotatedLocalAxes();

        // 2. Build rotation matrix
        const rotationMatrix = new Matrix3(
            dirs.x.x, dirs.y.x, dirs.z.x,
            dirs.x.y, dirs.y.y, dirs.z.y,
            dirs.x.z, dirs.y.z, dirs.z.z
        );

        // 3. Build world-space combined matrix (translation + rotation)
        const transformMatrix = Matrix4.fromRotationTranslation(rotationMatrix, this._planePosition);

        // 4. Calculate camera-distance-based scales
        const cameraPosition = this._viewer.camera.position;
        const distance = Cartesian3.distance(cameraPosition, this._planePosition);

        const S_arrow = Math.max(1.0, distance * GIZMO_STYLE.SCALE_FACTOR_ARROW);
        const S_plane = Math.max(1.0, distance * GIZMO_STYLE.SCALE_FACTOR_PLANE);

        // 5. Compute combined model matrices with local origin offset correction
        // GIZMO_OFFSET is Cartesian3(6378137.0, 0.0, 0.0), so invOffsetMatrix is translation by -6378137.0
        const invOffsetMatrix = Matrix4.fromTranslation(new Cartesian3(-GIZMO_MATH.EARTH_RADIUS_OFFSET, 0.0, 0.0), new Matrix4());

        const scaleArrow = Matrix4.fromScale(new Cartesian3(S_arrow, S_arrow, S_arrow));
        const arrowModelMatrix = Matrix4.multiply(transformMatrix, scaleArrow, new Matrix4());
        const actualArrowModelMatrix = Matrix4.multiply(arrowModelMatrix, invOffsetMatrix, new Matrix4());

        const scalePlane = Matrix4.fromScale(new Cartesian3(S_plane, S_plane, 1.0));
        const planeModelMatrix = Matrix4.multiply(transformMatrix, scalePlane, new Matrix4());
        const actualPlaneModelMatrix = Matrix4.multiply(planeModelMatrix, invOffsetMatrix, new Matrix4());

        // 6. Cập nhật modelMatrix của các Primitives (sử dụng kích thước khoảng cách camera chuẩn ổn định)
        if (this._planePrimitive) {
            this._planePrimitive.modelMatrix = actualPlaneModelMatrix;
            this._planePrimitive.show = this._isGizmoVisible;
        }
        if (this._planeOutlinePrimitive) {
            this._planeOutlinePrimitive.modelMatrix = actualPlaneModelMatrix;
            this._planeOutlinePrimitive.show = this._isGizmoVisible;
        }

        // Helper để cập nhật ma trận biến đổi, trạng thái hiển thị (show/hide), và màu sắc động cho cả bản normal và hover
        const updateGizmoComponent = (
            normalPrimitive: Primitive | undefined,
            hoverPrimitive: Primitive | undefined,
            isHovered: boolean,
            modelMatrix: Matrix4,
            normalColor: Color,
            hoverColor: Color
        ) => {
            if (normalPrimitive) {
                normalPrimitive.modelMatrix = modelMatrix;
                normalPrimitive.show = this._isGizmoVisible && !isHovered;
                if (normalPrimitive.appearance) {
                    normalPrimitive.appearance.material.uniforms.color = normalColor;
                }
            }
            if (hoverPrimitive) {
                hoverPrimitive.modelMatrix = modelMatrix;
                hoverPrimitive.show = this._isGizmoVisible && isHovered;
                if (hoverPrimitive.appearance) {
                    hoverPrimitive.appearance.material.uniforms.color = hoverColor;
                }
            }
        };

        // 7. Cập nhật trạng thái hiển thị & ma trận cho từng mũi tên tịnh tiến
        updateGizmoComponent(
            this._arrowXPrimitive,
            this._arrowXHoverPrimitive,
            this._hoveredAxis === "x",
            actualArrowModelMatrix,
            GIZMO_STYLE.COLOR_ARROW_X,
            GIZMO_STYLE.COLOR_ARROW_X_HOVER
        );
        updateGizmoComponent(
            this._arrowYPrimitive,
            this._arrowYHoverPrimitive,
            this._hoveredAxis === "y",
            actualArrowModelMatrix,
            GIZMO_STYLE.COLOR_ARROW_Y,
            GIZMO_STYLE.COLOR_ARROW_Y_HOVER
        );
        updateGizmoComponent(
            this._arrowZPrimitive,
            this._arrowZHoverPrimitive,
            this._hoveredAxis === "z",
            actualArrowModelMatrix,
            GIZMO_STYLE.COLOR_ARROW_Z,
            GIZMO_STYLE.COLOR_ARROW_Z_HOVER
        );

        // 8. Cập nhật trạng thái hiển thị & ma trận cho từng vòng xoay tương tác
        updateGizmoComponent(
            this._ringXPrimitive,
            this._ringXHoverPrimitive,
            this._hoveredAxis === "rot-x",
            actualArrowModelMatrix,
            GIZMO_STYLE.COLOR_RING_X,
            GIZMO_STYLE.COLOR_RING_X_HOVER
        );
        updateGizmoComponent(
            this._ringYPrimitive,
            this._ringYHoverPrimitive,
            this._hoveredAxis === "rot-y",
            actualArrowModelMatrix,
            GIZMO_STYLE.COLOR_RING_Y,
            GIZMO_STYLE.COLOR_RING_Y_HOVER
        );
        updateGizmoComponent(
            this._ringZPrimitive,
            this._ringZHoverPrimitive,
            this._hoveredAxis === "rot-z",
            actualArrowModelMatrix,
            GIZMO_STYLE.COLOR_RING_Z,
            GIZMO_STYLE.COLOR_RING_Z_HOVER
        );
    };

    /**
     * Recreates gizmo visuals and reinitializes clipping using current state
     * (without recalculating rotation/normal - used when restoring saved state)
     */
    private _recreateGizmoVisualsAndClipping = () => {
        if (!defined(this._basePosition) || !defined(this._normal) || !defined(this._planeRotation)) {
            return;
        }

        // Clear cached matrices
        this._primitiveInverseMatrices.clear();

        // Remove old gizmo primitives
        this._removeGizmoPrimitives();

        // Offset matrix for geometry translation
        const offsetMatrix = Matrix4.fromTranslation(new Cartesian3(GIZMO_MATH.EARTH_RADIUS_OFFSET, 0.0, 0.0), new Matrix4());

        // 1. Create plane and outline
        const planeGeometryParam = new PlaneGeometry({
            vertexFormat: PerInstanceColorAppearance.VERTEX_FORMAT
        });
        const planeGeometry = PlaneGeometry.createGeometry(planeGeometryParam);
        if (defined(planeGeometry)) {
            const planeInstance = new GeometryInstance({
                geometry: planeGeometry,
                id: GIZMO_IDS.PLANE,
                attributes: {
                    color: ColorGeometryInstanceAttribute.fromColor(GIZMO_STYLE.COLOR_PLANE)
                },
                modelMatrix: offsetMatrix
            });
            GeometryPipeline.transformToWorldCoordinates(planeInstance);

            this._planePrimitive = new Primitive({
                geometryInstances: planeInstance,
                appearance: new PerInstanceColorAppearance({
                    flat: true,
                    translucent: false,
                    renderState: {
                        depthTest: { enabled: false },
                        depthMask: false,
                        blending: BlendingState.ALPHA_BLEND
                    }
                }),
                asynchronous: false
            });
            this._viewer.scene.primitives.add(this._planePrimitive);
        }

        const planeOutlineGeometryParam = new PlaneOutlineGeometry();
        const planeOutlineGeometry = (PlaneOutlineGeometry as any).createGeometry(planeOutlineGeometryParam);
        if (defined(planeOutlineGeometry)) {
            const planeOutlineInstance = new GeometryInstance({
                geometry: planeOutlineGeometry,
                id: GIZMO_IDS.PLANE_OUTLINE,
                attributes: {
                    color: ColorGeometryInstanceAttribute.fromColor(GIZMO_STYLE.COLOR_PLANE_OUTLINE)
                },
                modelMatrix: offsetMatrix
            });
            GeometryPipeline.transformToWorldCoordinates(planeOutlineInstance);

            this._planeOutlinePrimitive = new Primitive({
                geometryInstances: planeOutlineInstance,
                appearance: new PerInstanceColorAppearance({
                    flat: true,
                    translucent: false,
                    renderState: {
                        depthTest: { enabled: false },
                        depthMask: false
                    }
                }),
                asynchronous: false
            });
            this._viewer.scene.primitives.add(this._planeOutlinePrimitive);
        }

        // 2. Create arrows (X, Y, Z)
        this._arrowXPrimitive = this._createPolylineGizmo(
            [new Cartesian3(0, 0, 0), new Cartesian3(1, 0, 0)],
            GIZMO_STYLE.ARROW_WIDTH,
            GIZMO_IDS.ARROW_X,
            GIZMO_STYLE.COLOR_ARROW_X,
            offsetMatrix,
            true
        );
        this._arrowXHoverPrimitive = this._createPolylineGizmo(
            [new Cartesian3(0, 0, 0), new Cartesian3(1, 0, 0)],
            GIZMO_STYLE.ARROW_WIDTH_HOVER,
            GIZMO_IDS.ARROW_X,
            GIZMO_STYLE.COLOR_ARROW_X_HOVER,
            offsetMatrix,
            true
        );

        this._arrowYPrimitive = this._createPolylineGizmo(
            [new Cartesian3(0, 0, 0), new Cartesian3(0, 1, 0)],
            GIZMO_STYLE.ARROW_WIDTH,
            GIZMO_IDS.ARROW_Y,
            GIZMO_STYLE.COLOR_ARROW_Y,
            offsetMatrix,
            true
        );
        this._arrowYHoverPrimitive = this._createPolylineGizmo(
            [new Cartesian3(0, 0, 0), new Cartesian3(0, 1, 0)],
            GIZMO_STYLE.ARROW_WIDTH_HOVER,
            GIZMO_IDS.ARROW_Y,
            GIZMO_STYLE.COLOR_ARROW_Y_HOVER,
            offsetMatrix,
            true
        );

        this._arrowZPrimitive = this._createPolylineGizmo(
            [new Cartesian3(0, 0, 0), new Cartesian3(0, 0, 1)],
            GIZMO_STYLE.ARROW_WIDTH,
            GIZMO_IDS.ARROW_Z,
            GIZMO_STYLE.COLOR_ARROW_Z,
            offsetMatrix,
            true
        );
        this._arrowZHoverPrimitive = this._createPolylineGizmo(
            [new Cartesian3(0, 0, 0), new Cartesian3(0, 0, 1)],
            GIZMO_STYLE.ARROW_WIDTH_HOVER,
            GIZMO_IDS.ARROW_Z,
            GIZMO_STYLE.COLOR_ARROW_Z_HOVER,
            offsetMatrix,
            true
        );

        // 3. Create rotation rings (X, Y, Z)
        this._ringXPrimitive = this._createPolylineGizmo(
            this._getLocalRingPoints("x"),
            GIZMO_STYLE.RING_WIDTH,
            GIZMO_IDS.RING_X,
            GIZMO_STYLE.COLOR_RING_X,
            offsetMatrix,
            false
        );
        this._ringXHoverPrimitive = this._createPolylineGizmo(
            this._getLocalRingPoints("x"),
            GIZMO_STYLE.RING_WIDTH_HOVER,
            GIZMO_IDS.RING_X,
            GIZMO_STYLE.COLOR_RING_X_HOVER,
            offsetMatrix,
            false
        );

        this._ringYPrimitive = this._createPolylineGizmo(
            this._getLocalRingPoints("y"),
            GIZMO_STYLE.RING_WIDTH,
            GIZMO_IDS.RING_Y,
            GIZMO_STYLE.COLOR_RING_Y,
            offsetMatrix,
            false
        );
        this._ringYHoverPrimitive = this._createPolylineGizmo(
            this._getLocalRingPoints("y"),
            GIZMO_STYLE.RING_WIDTH_HOVER,
            GIZMO_IDS.RING_Y,
            GIZMO_STYLE.COLOR_RING_Y_HOVER,
            offsetMatrix,
            false
        );

        this._ringZPrimitive = this._createPolylineGizmo(
            this._getLocalRingPoints("z"),
            GIZMO_STYLE.RING_WIDTH,
            GIZMO_IDS.RING_Z,
            GIZMO_STYLE.COLOR_RING_Z,
            offsetMatrix,
            false
        );
        this._ringZHoverPrimitive = this._createPolylineGizmo(
            this._getLocalRingPoints("z"),
            GIZMO_STYLE.RING_WIDTH_HOVER,
            GIZMO_IDS.RING_Z,
            GIZMO_STYLE.COLOR_RING_Z_HOVER,
            offsetMatrix,
            false
        );

        // 4. Set up preRender listener
        this._removeListener = this._viewer.scene.preRender.addEventListener(() => {
            this._updateGizmoModelMatrix();
            this._syncClippablePrimitives();
        });
        this._updateGizmoModelMatrix();

        // 5. Reinitialize clipping for all primitives
        const clippablePrimitives = this._findAllClippablePrimitives(this._viewer.scene.primitives);
        clippablePrimitives.forEach((primitive) => {
            this._initClippingForPrimitive(primitive);
        });

        this._viewer.scene.requestRender();
    }

    /**
     * Finds all primitives in the scene that can be clipped (Model or Cesium3DTileset),
     * including nested ones.
     */
    private _findAllClippablePrimitives = (collection: any, result: any[] = []): any[] => {
        if (!defined(collection)) return result;
        const length = collection.length;
        for (let i = 0; i < length; i++) {
            const primitive = collection.get(i);
            if (primitive instanceof Cesium3DTileset || primitive instanceof Model) {
                result.push(primitive);
            } else if (defined(primitive.show) && typeof primitive.get === 'function') {
                // Recursively search nested PrimitiveCollection
                this._findAllClippablePrimitives(primitive, result);
            }
        }
        return result;
    }

    /**
     * Initializes or toggles the clipping plane interaction.
     */
    createClipping = () => {
        if (this._isActive) {
            this.deactivate();
            return;
        }

        this._isActive = true;
        this._isGizmoVisible = true;

        if (this._statusStackItem) {
            this._statusStackItem.remove();
        }
        this._statusStackItem = showStatusStack(this._containerId, getMessage("clipping-plane-state"));

        // Restore saved gizmo state if available
        const hasRestoredState = this._restoreGizmoState();

        // Setup mouse handlers
        this._setupMouseHandlers();

        // If we restored a saved state, recreate the gizmo visuals WITHOUT recalculating rotation
        if (hasRestoredState && defined(this._basePosition)) {
            this._recreateGizmoVisualsAndClipping();
        }
    };

    private _setupMouseHandlers = () => {
        this._handler = new ScreenSpaceEventHandler(this._viewer.scene.canvas);

        // 1. LEFT_DOWN: Pick existing plane, X/Y/Z arrows, or X/Y/Z rotation rings for dragging, or place a new plane
        // Note: when interaction is paused we still allow starting a drag on existing gizmo
        // but we block any clicks that would create/place a new plane.
        this._handler.setInputAction((click: any) => {
            const pickedObjects = this._viewer.scene.drillPick(click.position);

            // Determine if the picked entity is the plane, an arrow, or a rotation ring
            let activeDragAxis: "x" | "y" | "z" | "plane" | "rot-x" | "rot-y" | "rot-z" | undefined = undefined;

            // Thứ tự ưu tiên: 1. Vòng xoay, 2. Mũi tên, 3. Mặt phẳng ảo
            const findPickedAxis = () => {
                // Thử tìm vòng xoay trước
                for (const picked of pickedObjects) {
                    if (defined(picked) && defined(picked.id)) {
                        const id = picked.id;
                        const idStr = typeof id === 'string' ? id : (id.id || "");
                        if (idStr === GIZMO_IDS.RING_X) return "rot-x";
                        if (idStr === GIZMO_IDS.RING_Y) return "rot-y";
                        if (idStr === GIZMO_IDS.RING_Z) return "rot-z";
                    }
                }
                // Thử tìm mũi tên
                for (const picked of pickedObjects) {
                    if (defined(picked) && defined(picked.id)) {
                        const id = picked.id;
                        const idStr = typeof id === 'string' ? id : (id.id || "");
                        if (idStr === GIZMO_IDS.ARROW_X) return "x";
                        if (idStr === GIZMO_IDS.ARROW_Y) return "y";
                        if (idStr === GIZMO_IDS.ARROW_Z) return "z";
                    }
                }
                // Cuối cùng mới đến mặt phẳng ảo (gồm mặt phẳng và outline của nó)
                for (const picked of pickedObjects) {
                    if (defined(picked) && defined(picked.id)) {
                        const id = picked.id;
                        const idStr = typeof id === 'string' ? id : (id.id || "");
                        if (idStr === GIZMO_IDS.PLANE || idStr === GIZMO_IDS.PLANE_OUTLINE) return "plane";
                    }
                }
                return undefined;
            };

            activeDragAxis = this._isGizmoVisible ? findPickedAxis() : undefined;

            // If interactions are paused and the click did not hit an existing gizmo,
            // ignore the click (prevent creating/placing a new plane).
            if (this._isInteractionPaused && !defined(activeDragAxis)) {
                return;
            }

            if (defined(activeDragAxis)) {
                this._isDragging = true;
                this._activeDragAxis = activeDragAxis;
                this._viewer.scene.screenSpaceCameraController.enableInputs = false;
                this._dragStartMousePosition = Cartesian2.clone(click.position, new Cartesian2());
                this._dragStartHeight = this._currentHeight;
                this._dragStartBasePosition = Cartesian3.clone(this._basePosition!, new Cartesian3());
                this._dragStartPlanePosition = Cartesian3.clone(this._planePosition, new Cartesian3());

                // Tính toán hệ trục trực chuẩn cục bộ (Local Orthonormal Axes) dựa trên helper
                const dirs = this._getRotatedLocalAxes();
                let localX = dirs.x;
                let localY = dirs.y;
                let localZ = dirs.z;

                // Gán trục pháp tuyến kéo dựa trên trục cục bộ đã xoay tương ứng
                if (activeDragAxis === "x" || activeDragAxis === "rot-x") {
                    this._dragNormal = localX;
                } else if (activeDragAxis === "y" || activeDragAxis === "rot-y") {
                    this._dragNormal = localY;
                } else if (activeDragAxis === "z" || activeDragAxis === "rot-z") {
                    this._dragNormal = localZ;
                } else {
                    this._dragNormal = this._normal; // Plane dragging translations follow active cutting normal
                }

                if (activeDragAxis.startsWith("rot-")) {
                    // Set up 2D rotation dial angle tracking
                    const centerScreen = SceneTransforms.worldToWindowCoordinates(this._viewer.scene, this._planePosition, new Cartesian2());
                    if (defined(centerScreen)) {
                        const startVector2D = Cartesian2.subtract(click.position, centerScreen, new Cartesian2());
                        this._dragStartAngle2D = Math.atan2(startVector2D.y, startVector2D.x);
                    } else {
                        this._dragStartAngle2D = 0.0;
                    }
                    this._dragStartNormal = Cartesian3.clone(this._normal!, new Cartesian3());
                    this._dragStartRotation = Matrix3.clone(this._planeRotation, new Matrix3());
                } else {
                    // Calculate 3D ray closest-point projection start along the selected drag axis (translation)
                    const ray = this._viewer.camera.getPickRay(click.position);
                    if (defined(ray) && defined(this._dragStartPlanePosition) && defined(this._dragNormal)) {
                        const cameraPosition = this._viewer.camera.position;
                        const w0 = Cartesian3.subtract(cameraPosition, this._dragStartPlanePosition, new Cartesian3());
                        const d = ray.direction;
                        const b = Cartesian3.dot(d, this._dragNormal);
                        const denom = 1.0 - b * b;
                        if (Math.abs(denom) > GIZMO_MATH.DENOMINATOR_TOLERANCE) {
                            this._dragStartS = (Cartesian3.dot(w0, this._dragNormal) - Cartesian3.dot(w0, d) * b) / denom;
                        } else {
                            this._dragStartS = 0.0;
                        }
                    } else {
                        this._dragStartS = 0.0;
                    }
                }

                // console.log(`Start dragging clipping plane along axis ${activeDragAxis} from height: `, this._currentHeight);
                return;
            }
        }, ScreenSpaceEventType.LEFT_DOWN);

        // 1.5. LEFT_DOUBLE_CLICK: Place/move the clipping plane to the newly double-clicked spot
        this._handler.setInputAction((click: any) => {
            // When interaction paused, do not allow placing a new clipping plane
            if (this._isInteractionPaused) return;
            let position: Cartesian3 | undefined = this._viewer.scene.pickPosition(click.position);

            if (defined(position)) {
                const pickedObject = this._viewer.scene.drillPick(click.position);
                if (pickedObject.length === 0) {
                    const ray = this._viewer.camera.getPickRay(click.position);
                    if (defined(ray)) {
                        const positionGlobe = this._viewer.scene.globe.pick(ray, this._viewer.scene);
                        if (defined(positionGlobe)) {
                            position = positionGlobe;
                        }
                    }
                }
            } else {
                const ray = this._viewer.camera.getPickRay(click.position);
                if (defined(ray)) {
                    const positionGlobe = this._viewer.scene.globe.pick(ray, this._viewer.scene);
                    if (defined(positionGlobe)) {
                        position = positionGlobe;
                    }
                }
            }

            if (!defined(position)) {
                const positionEllipsoid = this._viewer.camera.pickEllipsoid(click.position, this._viewer.scene.globe.ellipsoid);
                if (defined(positionEllipsoid)) {
                    position = positionEllipsoid;
                }
            }

            // Fallback for clicking on sky/outer space: try picking at the center of the screen
            if (!defined(position)) {
                const centerCanvas = new Cartesian2(this._viewer.canvas.clientWidth / 2, this._viewer.canvas.clientHeight / 2);
                let positionCenter: Cartesian3 | undefined = this._viewer.scene.pickPosition(centerCanvas);
                if (defined(positionCenter)) {
                    const pickedObjectCenter = this._viewer.scene.drillPick(centerCanvas);
                    if (pickedObjectCenter.length === 0) {
                        const rayCenter = this._viewer.camera.getPickRay(centerCanvas);
                        if (defined(rayCenter)) {
                            const positionGlobeCenter = this._viewer.scene.globe.pick(rayCenter, this._viewer.scene);
                            if (defined(positionGlobeCenter)) {
                                positionCenter = positionGlobeCenter;
                            }
                        }
                    }
                } else {
                    const rayCenter = this._viewer.camera.getPickRay(centerCanvas);
                    if (defined(rayCenter)) {
                        const positionGlobeCenter = this._viewer.scene.globe.pick(rayCenter, this._viewer.scene);
                        if (defined(positionGlobeCenter)) {
                            positionCenter = positionGlobeCenter;
                        }
                    }
                }
                if (!defined(positionCenter)) {
                    positionCenter = this._viewer.camera.pickEllipsoid(centerCanvas, this._viewer.scene.globe.ellipsoid);
                }
                if (defined(positionCenter)) {
                    position = positionCenter;
                }
            }

            // Absolute fallback: project the camera's current position to the surface of the ellipsoid
            if (!defined(position)) {
                const cameraPosition = this._viewer.camera.position;
                const positionBelowCamera = this._viewer.scene.globe.ellipsoid.scaleToGeodeticSurface(cameraPosition);
                if (defined(positionBelowCamera)) {
                    position = positionBelowCamera;
                }
            }

            if (defined(position)) {
                // console.log("Placing clipping plane at world position: ", position);
                this._currentHeight = 0.0; // Reset height offset to 0 for new location
                this._mode = "up"; // Always default/reset to Z mode when double click places a new plane
                this._isClippingActive = true;
                this.applyClippingPlane(position);
                if (this.onPositionPlaced) {
                    this.onPositionPlaced();
                }
            }
        }, ScreenSpaceEventType.LEFT_DOUBLE_CLICK);

        // 2. MOUSE_MOVE: Update height, translation, or rotation of clipping plane
        // Allow MOUSE_MOVE while paused only if an active drag is in progress
        this._handler.setInputAction((movement: any) => {
            if (this._isInteractionPaused && !this._isDragging) return;
            if (!this._isActive) {
                return;
            }

            if (!this._isDragging) {
                // Nhận diện đối tượng đang hover khi chưa kéo thả
                let hoveredAxis: "x" | "y" | "z" | "rot-x" | "rot-y" | "rot-z" | undefined = undefined;
                if (defined(this._planePrimitive)) {
                    const pickedObjects = this._viewer.scene.drillPick(movement.endPosition);
                    // Ưu tiên phát hiện các vòng xoay trước, sau đó tới các mũi tên
                    for (const picked of pickedObjects) {
                        if (defined(picked) && defined(picked.id)) {
                            const id = picked.id;
                            const idStr = typeof id === 'string' ? id : (id.id || "");
                            if (idStr === GIZMO_IDS.RING_X) { hoveredAxis = "rot-x"; break; }
                            if (idStr === GIZMO_IDS.RING_Y) { hoveredAxis = "rot-y"; break; }
                            if (idStr === GIZMO_IDS.RING_Z) { hoveredAxis = "rot-z"; break; }
                        }
                    }
                    if (!defined(hoveredAxis)) {
                        for (const picked of pickedObjects) {
                            if (defined(picked) && defined(picked.id)) {
                                const id = picked.id;
                                const idStr = typeof id === 'string' ? id : (id.id || "");
                                if (idStr === GIZMO_IDS.ARROW_X) { hoveredAxis = "x"; break; }
                                if (idStr === GIZMO_IDS.ARROW_Y) { hoveredAxis = "y"; break; }
                                if (idStr === GIZMO_IDS.ARROW_Z) { hoveredAxis = "z"; break; }
                            }
                        }
                    }
                }

                if (this._hoveredAxis !== hoveredAxis) {
                    this._hoveredAxis = hoveredAxis;
                    this._updateGizmoModelMatrix();
                    this._viewer.scene.requestRender();
                }
                return;
            }

            // Nếu đang kéo thả, kiểm tra các trạng thái cần thiết
            if (!defined(this._basePosition) || !defined(this._normal) || !defined(this._planePrimitive) || !defined(this._activeDragAxis) || !defined(this._dragNormal)) {
                return;
            }

            // Đảm bảo hoveredAxis đồng bộ với activeDragAxis khi đang kéo
            const activeHoverAxis = this._activeDragAxis === "plane" ? undefined : this._activeDragAxis;
            if (this._hoveredAxis !== activeHoverAxis) {
                this._hoveredAxis = activeHoverAxis;
                this._updateGizmoModelMatrix();
            }

            const ray = this._viewer.camera.getPickRay(movement.endPosition);

            if (this._activeDragAxis.startsWith("rot-")) {
                const centerScreen = SceneTransforms.worldToWindowCoordinates(this._viewer.scene, this._planePosition, new Cartesian2());
                if (defined(centerScreen) && defined(this._dragStartNormal) && defined(this._dragNormal) && defined(this._dragStartRotation)) {
                    const currentVector2D = Cartesian2.subtract(movement.endPosition, centerScreen, new Cartesian2());
                    const angleCurrent = Math.atan2(currentVector2D.y, currentVector2D.x);

                    // Tính góc xoay lệch so với góc click ban đầu (đảo ngược lại hướng xoay để khớp với kéo chuột)
                    let angle = -(angleCurrent - this._dragStartAngle2D);

                    // Xác định chiều xoay dựa trên hướng camera so với trục xoay cục bộ
                    // camera.direction ngược chiều với trục xoay -> angle quay thuận
                    // camera.direction cùng chiều với trục xoay -> angle quay ngược
                    const dotCamera = Cartesian3.dot(this._viewer.camera.direction, this._dragNormal);
                    if (dotCamera > 0.0) {
                        angle = -angle; // Đảo ngược góc xoay nếu nhìn từ phía sau trục xoay
                    }

                    // Thực hiện xoay pháp tuyến ban đầu quanh trục xoay cục bộ
                    const quat = Quaternion.fromAxisAngle(this._dragNormal, angle, new Quaternion());
                    const rotMatrix = Matrix3.fromQuaternion(quat, new Matrix3());

                    // Cập nhật ma trận xoay cục bộ ổn định
                    this._planeRotation = Matrix3.multiply(rotMatrix, this._dragStartRotation, new Matrix3());
                    this._normal = Matrix3.getColumn(this._planeRotation, 2, new Cartesian3());

                    // Reset currentHeight and update basePosition to prevent jumping on subsequent translations
                    this._basePosition = Cartesian3.clone(this._planePosition, new Cartesian3());
                    this._currentHeight = 0.0;
                }
            } else {
                let deltaS = 0.0;
                let used3DDrag = false;

                if (defined(ray) && defined(this._dragStartPlanePosition)) {
                    const cameraPosition = this._viewer.camera.position;
                    const w0 = Cartesian3.subtract(cameraPosition, this._dragStartPlanePosition, new Cartesian3());
                    const d = ray.direction;
                    const b = Cartesian3.dot(d, this._dragNormal);
                    const denom = 1.0 - b * b;

                    if (Math.abs(denom) > GIZMO_MATH.DENOMINATOR_TOLERANCE) {
                        const s = (Cartesian3.dot(w0, this._dragNormal) - Cartesian3.dot(w0, d) * b) / denom;
                        deltaS = s - this._dragStartS;
                        used3DDrag = true;
                    }
                }

                if (!used3DDrag) {
                    // Fallback to screen space delta dragging
                    const deltaY = this._dragStartMousePosition.y - movement.endPosition.y;
                    const cameraDistance = Cartesian3.distance(this._viewer.camera.position, this._planePosition);
                    const scale = cameraDistance * GIZMO_MATH.SCREEN_DRAG_SCALE;
                    deltaS = deltaY * scale;
                }

                const isActiveAxis = (this._activeDragAxis === "plane" || this._activeDragAxis === "z");

                if (isActiveAxis) {
                    // Translating along the active cutting axis: updates _currentHeight and clips the model
                    this._currentHeight = this._dragStartHeight + deltaS;

                    // Recalculate _planePosition based on the start base position and new height offset
                    const offset = Cartesian3.multiplyByScalar(this._normal, this._currentHeight, new Cartesian3());
                    this._planePosition = Cartesian3.add(this._basePosition, offset, new Cartesian3());
                } else {
                    // Translating along a perpendicular axis: updates _basePosition and moves the gizmo visually
                    const translation = Cartesian3.multiplyByScalar(this._dragNormal, deltaS, new Cartesian3());
                    this._basePosition = Cartesian3.add(this._dragStartBasePosition!, translation, new Cartesian3());

                    // Update _planePosition to match the new base position plus the current height offset
                    const offset = Cartesian3.multiplyByScalar(this._normal, this._currentHeight, new Cartesian3());
                    this._planePosition = Cartesian3.add(this._basePosition, offset, new Cartesian3());
                }
            }

            // Cập nhật khoảng cách và pháp tuyến của mặt phẳng cắt cho tất cả các mô hình trong phân cảnh
            const clippablePrimitives = this._findAllClippablePrimitives(this._viewer.scene.primitives);
            clippablePrimitives.forEach((primitive) => {
                if (defined(primitive.clippingPlanes) && primitive.clippingPlanes.length > 0) {
                    const plane = primitive.clippingPlanes.get(0);

                    // Luôn tính lại originMatrix trực tiếp từ primitive thay vì dùng cache cũ
                    // (Với Cesium3DTileset, clippingPlanesOriginMatrix = modelMatrix × _initial... phải dùng public getter)
                    let originMatrix = Matrix4.IDENTITY;
                    if (primitive instanceof Cesium3DTileset) {
                        const tilesetAny = primitive as any;
                        if (defined(tilesetAny.clippingPlanesOriginMatrix)) {
                            // ✅ Đúng: bao gồm cả tileset.modelMatrix
                            originMatrix = tilesetAny.clippingPlanesOriginMatrix;
                        } else if (defined(primitive.root) && defined((primitive as any).root.computedTransform)) {
                            originMatrix = (primitive as any).root.computedTransform;
                        }
                    } else if (primitive instanceof Model) {
                        if (defined(primitive.modelMatrix)) {
                            originMatrix = primitive.modelMatrix;
                        }
                    }

                    const inverseMatrix = Matrix4.inverse(originMatrix, new Matrix4());
                    if (!defined(inverseMatrix)) return;

                    // Cập nhật cache để đồng bộ
                    this._primitiveInverseMatrices.set(primitive, inverseMatrix);

                    // Tính toán lại pháp tuyến cục bộ dựa trên pháp tuyến thế giới đã xoay (this._normal)
                    const worldDownwardNormal = Cartesian3.negate(this._normal!, new Cartesian3());
                    const transposeMatrix = Matrix4.transpose(originMatrix, new Matrix4());
                    const localNormalUnnormalized = Matrix4.multiplyByPointAsVector(transposeMatrix, worldDownwardNormal, new Cartesian3());
                    if (Cartesian3.magnitude(localNormalUnnormalized) > GIZMO_MATH.NORMAL_TOLERANCE) {
                        const localNormal = Cartesian3.normalize(localNormalUnnormalized, new Cartesian3());
                        plane.normal = localNormal;
                    }

                    // Chuyển đổi vị trí mặt phẳng sang không gian cục bộ
                    const localPosition = Matrix4.multiplyByPoint(inverseMatrix, this._planePosition, new Cartesian3());

                    // Tính toán lại khoảng cách cục bộ
                    plane.distance = -Cartesian3.dot(plane.normal, localPosition);
                }
            });

            this._viewer.scene.requestRender();
        }, ScreenSpaceEventType.MOUSE_MOVE);

        // 3. LEFT_UP: Release drag operation and re-enable camera controls
        // Allow LEFT_UP to complete the drag even if interaction paused
        this._handler.setInputAction(() => {
            if (this._isInteractionPaused && !this._isDragging) return;
            if (this._isDragging) {
                const wasRotation = this._activeDragAxis && this._activeDragAxis.startsWith("rot-");
                this._isDragging = false;
                this._activeDragAxis = undefined;
                this._hoveredAxis = undefined; // Reset hover state on release
                this._dragNormal = undefined;
                this._dragStartBasePosition = undefined;
                this._dragStartNormal = undefined;
                this._dragStartRotation = undefined;
                this._dragStartPlanePosition = undefined;
                this._viewer.scene.screenSpaceCameraController.enableInputs = true;

                if (wasRotation) {
                    this._basePosition = Cartesian3.clone(this._planePosition, new Cartesian3());
                    this._currentHeight = 0.0;
                }

                this._updateGizmoModelMatrix();
                this._viewer.scene.requestRender();
                // console.log("Released clipping plane at height/rotation: ", this._currentHeight);
            }
        }, ScreenSpaceEventType.LEFT_UP);
    }

    /**
     * Applies a horizontal clipping plane at the specified world position
     * and clips all models and 3D Tilesets in the scene.
     */
    applyClippingPlane = (position: Cartesian3) => {
        this._basePosition = position;
        this._planePosition = Cartesian3.clone(position, new Cartesian3());

        const enuMatrix = Transforms.eastNorthUpToFixedFrame(position, this._viewer.scene.globe.ellipsoid, new Matrix4());
        const enuRotation = Matrix4.getMatrix3(enuMatrix, new Matrix3());

        if (this._mode === "east") {
            const rotationY = Matrix3.fromRotationY(Math.PI / 2, new Matrix3());
            this._planeRotation = Matrix3.multiply(enuRotation, rotationY, new Matrix3());
        } else if (this._mode === "north") {
            const rotationX = Matrix3.fromRotationX(-Math.PI / 2, new Matrix3());
            this._planeRotation = Matrix3.multiply(enuRotation, rotationX, new Matrix3());
        } else {
            this._planeRotation = Matrix3.clone(enuRotation, new Matrix3());
        }

        this._normal = Matrix3.getColumn(this._planeRotation, 2, new Cartesian3());

        // Clear cached matrices
        this._primitiveInverseMatrices.clear();

        // Xóa các primitives cũ nếu chúng tồn tại
        this._removeGizmoPrimitives();

        // Định nghĩa ma trận dịch chuyển để dịch toàn bộ Geometries ra xa tâm Trái Đất (đến x = 6,378,137m trên equator)
        // Việc này ngăn ngừa lỗi "Could not project point (0,0,0) to 2D" khi Cesium chuẩn bị hình học cho chế độ 2D/Columbus View.
        const offsetMatrix = Matrix4.fromTranslation(new Cartesian3(GIZMO_MATH.EARTH_RADIUS_OFFSET, 0.0, 0.0), new Matrix4());

        // 1. Tạo mặt phẳng ảo bán trong suốt và outline của nó
        const planeGeometryParam = new PlaneGeometry({
            vertexFormat: PerInstanceColorAppearance.VERTEX_FORMAT
        });
        const planeGeometry = PlaneGeometry.createGeometry(planeGeometryParam);
        if (!defined(planeGeometry)) return;

        const planeInstance = new GeometryInstance({
            geometry: planeGeometry,
            id: GIZMO_IDS.PLANE,
            attributes: {
                color: ColorGeometryInstanceAttribute.fromColor(GIZMO_STYLE.COLOR_PLANE)
            },
            modelMatrix: offsetMatrix
        });
        GeometryPipeline.transformToWorldCoordinates(planeInstance);

        this._planePrimitive = new Primitive({
            geometryInstances: planeInstance,
            appearance: new PerInstanceColorAppearance({
                flat: true,
                translucent: false,
                renderState: {
                    depthTest: {
                        enabled: false // TẮT depth test để mặt phẳng cắt ảo luôn hiển thị trên cùng không bị che khuất
                    },
                    depthMask: false,
                    blending: BlendingState.ALPHA_BLEND
                }
            }),
            asynchronous: false
        });
        this._viewer.scene.primitives.add(this._planePrimitive);

        const planeOutlineGeometryParam = new PlaneOutlineGeometry();
        const planeOutlineGeometry = (PlaneOutlineGeometry as any).createGeometry(planeOutlineGeometryParam);
        if (!defined(planeOutlineGeometry)) return;

        const planeOutlineInstance = new GeometryInstance({
            geometry: planeOutlineGeometry,
            id: GIZMO_IDS.PLANE_OUTLINE,
            attributes: {
                color: ColorGeometryInstanceAttribute.fromColor(GIZMO_STYLE.COLOR_PLANE_OUTLINE)
            },
            modelMatrix: offsetMatrix
        });
        GeometryPipeline.transformToWorldCoordinates(planeOutlineInstance);

        this._planeOutlinePrimitive = new Primitive({
            geometryInstances: planeOutlineInstance,
            appearance: new PerInstanceColorAppearance({
                flat: true,
                translucent: false,
                renderState: {
                    depthTest: {
                        enabled: false // TẮT depth test để outline của mặt cắt ảo luôn hiển thị trên cùng
                    },
                    depthMask: false
                }
            }),
            asynchronous: false
        });
        this._viewer.scene.primitives.add(this._planeOutlinePrimitive);

        // 2. Tạo 3 Mũi tên tịnh tiến cục bộ (X: Đỏ, Y: Xanh lá, Z: Xanh dương) - TẮT depthTest để không bị che bởi BIM
        // Tạo cả phiên bản bình thường và phiên bản hover (độ dày lớn hơn)
        this._arrowXPrimitive = this._createPolylineGizmo(
            [new Cartesian3(0, 0, 0), new Cartesian3(1, 0, 0)],
            GIZMO_STYLE.ARROW_WIDTH,
            GIZMO_IDS.ARROW_X,
            GIZMO_STYLE.COLOR_ARROW_X,
            offsetMatrix,
            true
        );
        this._arrowXHoverPrimitive = this._createPolylineGizmo(
            [new Cartesian3(0, 0, 0), new Cartesian3(1, 0, 0)],
            GIZMO_STYLE.ARROW_WIDTH_HOVER,
            GIZMO_IDS.ARROW_X,
            GIZMO_STYLE.COLOR_ARROW_X_HOVER,
            offsetMatrix,
            true
        );

        this._arrowYPrimitive = this._createPolylineGizmo(
            [new Cartesian3(0, 0, 0), new Cartesian3(0, 1, 0)],
            GIZMO_STYLE.ARROW_WIDTH,
            GIZMO_IDS.ARROW_Y,
            GIZMO_STYLE.COLOR_ARROW_Y,
            offsetMatrix,
            true
        );
        this._arrowYHoverPrimitive = this._createPolylineGizmo(
            [new Cartesian3(0, 0, 0), new Cartesian3(0, 1, 0)],
            GIZMO_STYLE.ARROW_WIDTH_HOVER,
            GIZMO_IDS.ARROW_Y,
            GIZMO_STYLE.COLOR_ARROW_Y_HOVER,
            offsetMatrix,
            true
        );

        this._arrowZPrimitive = this._createPolylineGizmo(
            [new Cartesian3(0, 0, 0), new Cartesian3(0, 0, 1)],
            GIZMO_STYLE.ARROW_WIDTH,
            GIZMO_IDS.ARROW_Z,
            GIZMO_STYLE.COLOR_ARROW_Z,
            offsetMatrix,
            true
        );
        this._arrowZHoverPrimitive = this._createPolylineGizmo(
            [new Cartesian3(0, 0, 0), new Cartesian3(0, 0, 1)],
            GIZMO_STYLE.ARROW_WIDTH_HOVER,
            GIZMO_IDS.ARROW_Z,
            GIZMO_STYLE.COLOR_ARROW_Z_HOVER,
            offsetMatrix,
            true
        );

        // 3. Tạo 3 Vòng xoay cung 1/4 cục bộ (X: Xanh lá, Y: Đỏ, Z: Xanh dương) - TẮT depthTest
        // Tạo cả phiên bản bình thường và phiên bản hover (độ dày lớn hơn)
        this._ringXPrimitive = this._createPolylineGizmo(
            this._getLocalRingPoints("x"),
            GIZMO_STYLE.RING_WIDTH,
            GIZMO_IDS.RING_X,
            GIZMO_STYLE.COLOR_RING_X,
            offsetMatrix,
            false
        );
        this._ringXHoverPrimitive = this._createPolylineGizmo(
            this._getLocalRingPoints("x"),
            GIZMO_STYLE.RING_WIDTH_HOVER,
            GIZMO_IDS.RING_X,
            GIZMO_STYLE.COLOR_RING_X_HOVER,
            offsetMatrix,
            false
        );

        this._ringYPrimitive = this._createPolylineGizmo(
            this._getLocalRingPoints("y"),
            GIZMO_STYLE.RING_WIDTH,
            GIZMO_IDS.RING_Y,
            GIZMO_STYLE.COLOR_RING_Y,
            offsetMatrix,
            false
        );
        this._ringYHoverPrimitive = this._createPolylineGizmo(
            this._getLocalRingPoints("y"),
            GIZMO_STYLE.RING_WIDTH_HOVER,
            GIZMO_IDS.RING_Y,
            GIZMO_STYLE.COLOR_RING_Y_HOVER,
            offsetMatrix,
            false
        );

        this._ringZPrimitive = this._createPolylineGizmo(
            this._getLocalRingPoints("z"),
            GIZMO_STYLE.RING_WIDTH,
            GIZMO_IDS.RING_Z,
            GIZMO_STYLE.COLOR_RING_Z,
            offsetMatrix,
            false
        );
        this._ringZHoverPrimitive = this._createPolylineGizmo(
            this._getLocalRingPoints("z"),
            GIZMO_STYLE.RING_WIDTH_HOVER,
            GIZMO_IDS.RING_Z,
            GIZMO_STYLE.COLOR_RING_Z_HOVER,
            offsetMatrix,
            false
        );

        // 4. Lắng nghe sự kiện preRender để tự động định vị và co giãn kích thước
        this._removeListener = this._viewer.scene.preRender.addEventListener(() => {
            this._updateGizmoModelMatrix();
            this._syncClippablePrimitives();
        });
        this._updateGizmoModelMatrix();

        // 2. Clip all clippable primitives (Cesium3DTileset and Model) in the scene
        const clippablePrimitives = this._findAllClippablePrimitives(this._viewer.scene.primitives);

        clippablePrimitives.forEach((primitive) => {
            this._initClippingForPrimitive(primitive);
        });

        this._viewer.scene.requestRender();
    }

    private _initClippingForPrimitive = (primitive: any) => {
        // Get the origin/transform matrix of the primitive for clipping planes.
        // IMPORTANT: clippingPlanesOriginMatrix (public) = tileset.modelMatrix × _initialClippingPlanesOriginMatrix
        // We MUST use the public getter so the tileset's modelMatrix offset is included.
        let originMatrix = Matrix4.IDENTITY;
        if (primitive instanceof Cesium3DTileset) {
            const tilesetAny = primitive as any;
            if (defined(tilesetAny.clippingPlanesOriginMatrix)) {
                // ✅ Đúng: bao gồm cả tileset.modelMatrix
                originMatrix = tilesetAny.clippingPlanesOriginMatrix;
            } else if (defined(primitive.root) && defined(primitive.root.computedTransform)) {
                originMatrix = primitive.root.computedTransform;
            }
        } else if (primitive instanceof Model) {
            if (defined(primitive.modelMatrix)) {
                originMatrix = primitive.modelMatrix;
            }
        }

        // Calculate inverse of the primitive's origin matrix (including scale)
        const inverseMatrix = Matrix4.inverse(originMatrix, new Matrix4());
        if (!defined(inverseMatrix)) return;
        this._primitiveInverseMatrices.set(primitive, inverseMatrix);

        // Compute local downward normal for this primitive
        const worldDownwardNormal = Cartesian3.negate(this._normal!, new Cartesian3());
        const transposeMatrix = Matrix4.transpose(originMatrix, new Matrix4());
        const localNormalUnnormalized = Matrix4.multiplyByPointAsVector(transposeMatrix, worldDownwardNormal, new Cartesian3());

        if (Cartesian3.magnitude(localNormalUnnormalized) < GIZMO_MATH.NORMAL_TOLERANCE) return;
        const localNormal = Cartesian3.normalize(localNormalUnnormalized, new Cartesian3());

        // Compute local initial position using CURRENT _planePosition
        const localPosition = Matrix4.multiplyByPoint(inverseMatrix, this._planePosition, new Cartesian3());

        // Compute initial local distance
        const localDistance = -Cartesian3.dot(localNormal, localPosition);

        if (defined(primitive.clippingPlanes) && !primitive.clippingPlanes.isDestroyed()) {
            if (primitive.clippingPlanes.length > 0) {
                const existingPlane = primitive.clippingPlanes.get(0);
                existingPlane.normal = localNormal;
                existingPlane.distance = localDistance;
            } else {
                primitive.clippingPlanes.add(new ClippingPlane(localNormal, localDistance));
            }
            primitive.clippingPlanes.enabled = true;
        } else {
            // Create the local clipping plane
            const localClippingPlane = new ClippingPlane(localNormal, localDistance);
            // Assign the unique ClippingPlaneCollection to the primitive
            const collection = new ClippingPlaneCollection({
                planes: [localClippingPlane],
                modelMatrix: Matrix4.IDENTITY,
                edgeWidth: 1.5,
                edgeColor: Color.WHITE,
                unionClippingRegions: false
            });
            primitive.clippingPlanes = collection;

            // Khởi tạo ngay lập tức WebGL texture tài nguyên cho mặt phẳng cắt bằng cách gọi thủ công hàm update với frameState
            // Điều này ngăn chặn lỗi sụp đổ đồ họa "Cannot read properties of undefined (reading '_target')" khi thực hiện drillPick ngay sau đó
            const sceneAny = this._viewer.scene as any;
            if (defined(sceneAny.frameState)) {
                try {
                    (collection as any).update(sceneAny.frameState);
                } catch (e) {
                    // Safe fallback
                }
            }
        }
    };

    private _syncClippablePrimitives = () => {
        if (!this._isActive || !this._normal || !this._planePosition) return;

        const clippablePrimitives = this._findAllClippablePrimitives(this._viewer.scene.primitives);
        let hasNew = false;
        clippablePrimitives.forEach((primitive) => {
            if (!this._primitiveInverseMatrices.has(primitive)) {
                this._initClippingForPrimitive(primitive);
                hasNew = true;
            }
        });
        if (hasNew) {
            this._viewer.scene.requestRender();
        }
    };

    /**
     * Clears all clipping planes and removes visual representations from the scene.
     */
    deactivate = () => {
        // Save the current gizmo state before deactivating
        this._saveGizmoState();

        this._isActive = false;
        this._isDragging = false;
        this._isInteractionPaused = false;
        this._hoveredAxis = undefined;
        this._isGizmoVisible = true;
        this._mode = "up";

        if (this._statusStackItem) {
            this._statusStackItem.remove();
            this._statusStackItem = undefined;
        }

        // Clear cached matrices
        this._primitiveInverseMatrices.clear();

        // Ensure camera inputs are re-enabled
        if (this._viewer?.scene?.screenSpaceCameraController) {
            this._viewer.scene.screenSpaceCameraController.enableInputs = true;
        }

        // Destroy mouse click handler
        if (this._handler) {
            this._handler.destroy();
            this._handler = undefined;
        }

        // Clear preRender listener
        if (defined(this._removeListener)) {
            this._removeListener();
            this._removeListener = undefined;
        }

        // Xóa các primitives ảo, các mũi tên và các vòng xoay tương tác (cả bản bình thường và hover)
        this._removeGizmoPrimitives();

        // Reset clipping planes on all primitives safely to avoid WebGL rendering crashes
        const clippablePrimitives = this._findAllClippablePrimitives(this._viewer.scene.primitives);
        clippablePrimitives.forEach((primitive) => {
            if (defined(primitive.clippingPlanes) && !primitive.clippingPlanes.isDestroyed()) {
                primitive.clippingPlanes.enabled = false;
            }
        });

        this._viewer.scene.requestRender();
    }

    public setMode = (mode: "up" | "east" | "north") => {
        // If we just restored state, skip recalculation and clear the flag
        if (this._justRestoredState) {
            this._justRestoredState = false;
            return;  // Skip rotation recalculation - use restored state as-is
        }

        if (this._isActive && !this._statusStackItem) {
            this._statusStackItem = showStatusStack(this._containerId, getMessage("clipping-plane-state"));
        }

        this._mode = mode;
        this._currentHeight = 0.0; // Reset height to 0 to prevent sudden jump
        if (this._isActive && defined(this._basePosition)) {
            this._isClippingActive = true;
            this.applyClippingPlane(this._basePosition);
        }
    };

    public getMode = (): "up" | "east" | "north" => {
        return this._mode;
    };

    public showHideGizmo = (show: boolean) => {
        this._isGizmoVisible = show;
        this._updateGizmoModelMatrix();
        this._viewer.scene.requestRender();
    };

    public getGizmoVisibility = (): boolean => {
        return this._isGizmoVisible;
    };

    public resetClipping = () => {
        this._removeGizmoPrimitives();
        this._basePosition = undefined;
        this._planePosition = new Cartesian3();
        this._currentHeight = 0.0;
        this._isClippingActive = false;
        this._isInteractionPaused = false;

        // Clear saved state on hard reset
        this._savedBasePosition = undefined;
        this._savedPlanePosition = undefined;
        this._savedPlaneRotation = undefined;
        this._savedNormal = undefined;
        this._savedCurrentHeight = 0.0;
        this._savedMode = "up";

        const clippablePrimitives = this._findAllClippablePrimitives(this._viewer.scene.primitives);
        clippablePrimitives.forEach((primitive) => {
            if (defined(primitive.clippingPlanes) && !primitive.clippingPlanes.isDestroyed()) {
                primitive.clippingPlanes.enabled = false;
            }
        });

        this._viewer.scene.requestRender();
    };
}